/**
 * TaskErrorResolver — turn a failed task doc into actionable next steps.
 *
 * Two sources of actions, and nothing else:
 *
 *  1. **Built in** (G5) — a failed `generate-strategy` task. The module knows what
 *     such a run can become: «Save again» recovers a finished run that died while
 *     saving (G4a), «Generate again» continues an incomplete one or repeats it
 *     with its own parameters (G4b). Every answer — success, refusal, or a 409
 *     from the generation lock — is rendered inline, verbatim.
 *  2. **Registered** (AC-GP-029) — any other task type, but ONLY while a mounted
 *     surface registered a handler for it (`registerTaskResolveHandler`). Without
 *     one the resolver shows the message and «Open strategy», never a button whose
 *     click does nothing. `ContentPlansPage` registers the content-plan types.
 *
 * A registered handler is called directly; the legacy `task-error:resolve`
 * CustomEvent is still dispatched when nothing is registered, so an outside
 * listener (and the content-plan e2e specs, which dispatch the event themselves)
 * keeps working.
 */

import React from 'react';
import {
  Box, Stack, Typography, Button, Chip, Alert, IconButton, Tooltip,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SaveIcon from '@mui/icons-material/Save';
import { AgentApiClient } from '@modules/content-agent';
import { TASKS_PANEL } from '../selectors/tasks-panel.sel';
import {
  hasTaskResolveHandler,
  resolveTaskError,
  subscribeTaskResolveHandlers,
  type TaskResolveDetail,
} from '../services/taskResolveRegistry';
import {
  classifyStrategyTaskFailure,
  generateStrategyAgain,
  saveStrategyAgain,
  strategyActionLabel,
  fullGenerationRequestFromExecParams,
  type StrategyErrorTaskLike,
  type StrategyResolveApi,
  type StrategyTaskFailure,
} from '../services/strategyTaskResolve';
import { useTaskResolveAction } from '../hooks/useTaskResolveAction';

// ─── Typed view of the failure fields we care about ────────────────────

interface ErrorTaskLike extends StrategyErrorTaskLike {
  /** Stage B — per-theme shortfall emitted by StrategyPlanningOrchestrator */
  gapsByTheme?: Record<string, number>;
  /** Generic meta envelope (future-proofing for other codes) */
  errorMeta?: Record<string, unknown>;
}

export interface TaskErrorResolverProps {
  task: ErrorTaskLike;
  /** Optional override for the dispatch channel (tests) */
  eventName?: string;
  /** The dialog's navigation — «Open strategy» uses the task's own entity url. */
  onNavigate?: (frontendUrl: string) => void;
  /** Injected in tests; production uses the module's own builder API client. */
  api?: StrategyResolveApi;
}

// ─── Dispatch helper ───────────────────────────────────────────────────

/** The owner rule: a press that nothing acted on says so (fix round 1, I1). */
const NOTHING_ACTED = 'Nothing here can act on this failure — copy the task id and send it to support.';

/** A registered panel's press. `true` = some surface acted on it. */
type ResolveDispatch = (code: string, action: string, meta?: Record<string, unknown>) => void;

function dispatchResolve(
  eventName: string,
  code: string,
  action: string,
  task: ErrorTaskLike,
  meta?: Record<string, unknown>,
): boolean {
  const detail: TaskResolveDetail = { code, action, task: task as Record<string, unknown>, meta };
  // A registered handler owns the action; the event bus is the legacy fallback
  // (a surface that unregistered between render and click may still listen).
  if (resolveTaskError(detail)) return true;
  window.dispatchEvent(new CustomEvent(eventName, { detail }));
  return false;
}

// ─── Shared pieces ─────────────────────────────────────────────────────

const ErrorMessage: React.FC<{ title?: string; text: string }> = ({ title, text }) => (
  <Alert
    severity="error"
    sx={{ '& .MuiAlert-message': { width: '100%' } }}
    data-testid={TASKS_PANEL.errorResolverMessage}
  >
    {title && <Typography variant="subtitle2">{title}</Typography>}
    <Typography variant="body2" sx={{ mt: title ? 0.5 : 0, wordBreak: 'break-word' }}>
      {text}
    </Typography>
  </Alert>
);

/**
 * Ruling GP-R32 (G6a fix round 1, m6): the shared footer speaks the language of
 * the panel it is attached to. This module has NO i18n mechanism — every string
 * in this file, English and Russian alike, is a literal in the TSX (there is no
 * translation catalogue for admin chrome anywhere in the app; `locale-config` is
 * the CUSTOMER's funnel locales, not ours) — so «their own mechanism» is a
 * literal, chosen per panel. English panels (the strategy resolver, the generic
 * and message-only fallbacks) get English; the three Russian content-plan panels
 * get Russian. A bilingual action row is the defect this closes.
 *
 * When the app finally gets a catalogue, this is one of the call sites to move.
 */
type PanelLang = 'en' | 'ru';

/**
 * GP-R32 / final review FR-10: the error codes whose panel is written in RUSSIAN. They are
 * the content-plan panels below; every other panel of this module — the strategy resolver,
 * the generic fallback and the message-only fallback — is English.
 *
 * Kept as a set next to `ResolveRegistered`'s switch so ONE list decides both the panel that
 * renders and the language the dialog's section heading speaks (the jsdom tests assert both
 * ends for both languages).
 */
const RUSSIAN_PANEL_ERROR_CODES = new Set(['INSUFFICIENT_BACKLOG', 'EMPTY_SOURCE_DATA', 'AI_PARSE_FAILED']);

/**
 * FR-10 (Ruling GP-R32): which language the panel this task renders speaks — so the heading
 * ABOVE the panel («Error — what to do next») can follow it instead of standing in Russian
 * over an English resolver. Exact by construction: the branches below are this function's.
 */
export function taskErrorResolverPanelLang(
  task: ErrorTaskLike | null | undefined,
  /** Whether a mounted surface registered a handler for this task type (`ResolveRegistered`). */
  registered: boolean,
): PanelLang {
  if (!task) return 'en';
  // The built-in strategy resolver is English, whatever the task type registry says.
  if (classifyStrategyTaskFailure(task)) return 'en';
  // Without a registered handler the task gets `ResolveMessageOnly` — an English panel.
  if (!registered) return 'en';
  return RUSSIAN_PANEL_ERROR_CODES.has(task.errorCode ?? '') ? 'ru' : 'en';
}

/** The same answer for a live surface: it follows the registry exactly as the resolver does. */
export function useTaskErrorResolverPanelLang(task: ErrorTaskLike | null | undefined): PanelLang {
  const registered = React.useSyncExternalStore(
    subscribeTaskResolveHandlers,
    () => hasTaskResolveHandler(task?.taskType),
    () => false,
  );
  return taskErrorResolverPanelLang(task, registered);
}

/**
 * G5 fix round 1 (I2): the label names the entity the TASK made — a failed
 * research or content task opens a topic, not «a strategy». The selector kind
 * stays `open-strategy` so the registry keeps one name for this slot.
 *
 * The Russian label carries no entity noun: `entityType` is a machine string
 * (`strategy`, `screen`, …) and pasting it into a Russian sentence would only
 * move the language mix one word to the right.
 */
const OpenEntityButton: React.FC<{
  task: ErrorTaskLike;
  onNavigate?: (frontendUrl: string) => void;
  lang?: PanelLang;
}> = ({ task, onNavigate, lang = 'en' }) => {
  const url = task.targetEntityPath?.frontendUrl;
  if (!url || !onNavigate) return null;
  return (
    <Button
      variant="outlined"
      size="small"
      startIcon={<OpenInNewIcon />}
      onClick={() => onNavigate(url)}
      data-testid={TASKS_PANEL.errorResolverAction('open-strategy')}
    >
      {lang === 'ru' ? 'Открыть' : `Open ${task.targetEntityPath?.entityType ?? 'Result'}`}
    </Button>
  );
};

/**
 * G6a design review (finding n-GP-2, Must fix): the resolver row and the dialog's
 * own «Entity link» footer rendered the SAME outlined «Open …» button a divider
 * apart — and in the two states that offer no other action they were the only two
 * controls, pixel-identical. The resolver now owns that affordance for a FAILED
 * task (it sits where the decision is made, next to the primary action) and every
 * branch renders it, so this ONE predicate tells `TaskDetailDialog` to drop its
 * duplicate. Exact by construction: the button's own render conditions are these.
 */
export function taskErrorResolverOffersOpenEntity(
  task: {
    error?: string | null;
    errorCode?: string | null;
    targetEntityPath?: { frontendUrl?: string | null } | null;
  } | null | undefined,
  onNavigate?: (frontendUrl: string) => void,
): boolean {
  if (!task) return false;
  if (!task.error && !task.errorCode) return false;
  return Boolean(onNavigate && task.targetEntityPath?.frontendUrl);
}

/**
 * GP-R32 again: the tooltip IS this button's accessible name, so it was the one
 * Russian string sitting inside the English strategy panel. It now follows its
 * panel like the «Open …» label does.
 */
const CopyTaskIdButton: React.FC<{ task: ErrorTaskLike; lang?: PanelLang }> = ({ task, lang = 'en' }) => {
  if (!task.id) return null;
  return (
    <Tooltip title={lang === 'ru'
      ? 'Скопировать taskId для отчёта в support'
      : 'Copy task ID for a support report'}
    >
      <IconButton size="small" onClick={() => { void navigator.clipboard?.writeText(task.id!); }}>
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

// ─── Strategy generation (built in — G5) ───────────────────────────────

const ResolveStrategyGeneration: React.FC<{
  task: ErrorTaskLike;
  failure: StrategyTaskFailure;
  onNavigate?: (frontendUrl: string) => void;
  api?: StrategyResolveApi;
}> = ({ task, failure, onNavigate, api }) => {
  const { running, outcome, run } = useTaskResolveAction();
  const client = React.useMemo(() => api ?? (new AgentApiClient() as unknown as StrategyResolveApi), [api]);

  const kind = failure.kind === 'persistence' ? 'save-again' : 'generate-again';
  // Ruling GP-R20: a full regeneration is offered only when the run kept its own
  // parameters — never rebuilt from half-remembered fields.
  const canAct = failure.kind !== 'other' || Boolean(fullGenerationRequestFromExecParams(task.dispatch?.execParams));

  return (
    <Stack spacing={1}>
      <ErrorMessage text={failure.message || 'The generation failed.'} />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {canAct && (
          <Button
            variant="contained"
            size="small"
            startIcon={failure.kind === 'persistence' ? <SaveIcon /> : <ReplayIcon />}
            disabled={running !== null}
            onClick={() => run(kind, () => (failure.kind === 'persistence'
              ? saveStrategyAgain(task, client)
              : generateStrategyAgain(task, failure, client)))}
            data-testid={TASKS_PANEL.errorResolverAction(kind)}
          >
            {strategyActionLabel(failure.kind)}
          </Button>
        )}
        <OpenEntityButton task={task} onNavigate={onNavigate} />
        <CopyTaskIdButton task={task} />
      </Stack>
      {outcome && (
        <Alert severity={outcome.severity} data-testid={TASKS_PANEL.errorResolverResult}>
          {outcome.lines.map((line, index) => (
            <Typography key={`${index}-${line}`} variant="body2" sx={{ wordBreak: 'break-word' }}>
              {line}
            </Typography>
          ))}
        </Alert>
      )}
    </Stack>
  );
};

// ─── Per-code resolution panels (registered surfaces) ───────────────────

const ResolveInsufficientBacklog: React.FC<{
  task: ErrorTaskLike;
  dispatch: ResolveDispatch;
  onNavigate?: (frontendUrl: string) => void;
}> = ({ task, dispatch, onNavigate }) => {
  const gaps = task.gapsByTheme ?? (task.errorMeta?.gapsByTheme as Record<string, number> | undefined) ?? {};
  const totalMissing = Object.values(gaps).reduce((s, n) => s + (Number(n) || 0), 0);
  // Edge case: coverage check flagged gaps but rounded missingCount to 0
  // (floating-point weight aggregation on boundary ratios). The orchestrator
  // now floors cap≥1 so the actual retry will generate topics — but we
  // should still tell the user clearly that this was a rounding edge and
  // a plain one-shot retry will fix it.
  const isZeroEdge = totalMissing === 0;

  return (
    <Stack spacing={1.5}>
      <Alert severity="warning" icon={<LibraryBooksIcon />}>
        <Typography variant="subtitle2">Backlog не покрывает theme-квоты</Typography>
        {isZeroEdge ? (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Backlog покрывает квоты почти полностью, но на граничном соотношении
            (&lt;85%). Нажми <strong>«Перезапустить One-shot»</strong> — оркестратор
            догенерирует недостающие топики автоматически.
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Стратегии требуется примерно <strong>{totalMissing}</strong> дополнительных одобренных
            топиков. Они должны быть в статусе <code>ready</code>, разложены по темам ниже.
          </Typography>
        )}
      </Alert>

      {Object.keys(gaps).length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Нехватка по темам:
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {Object.entries(gaps).map(([themeId, count]) => (
              <Chip
                key={themeId}
                size="small"
                color="warning"
                variant="outlined"
                label={`${themeId} · -${count}`}
              />
            ))}
          </Stack>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary">
        Выбери одну из стратегий разрешения:
      </Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button
          variant="contained"
          size="small"
          startIcon={<AutoFixHighIcon />}
          onClick={() => dispatch('INSUFFICIENT_BACKLOG', 'retry-with-autofill', { gapsByTheme: gaps })}
        >
          Перезапустить One-shot
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<LibraryBooksIcon />}
          onClick={() => dispatch('INSUFFICIENT_BACKLOG', 'open-backlog', { gapsByTheme: gaps })}
        >
          Сгенерировать недостающие топики
        </Button>
        <OpenEntityButton task={task} onNavigate={onNavigate} lang="ru" />
        <CopyTaskIdButton task={task} lang="ru" />
      </Stack>
    </Stack>
  );
};

const ResolveEmptySourceData: React.FC<{
  task: ErrorTaskLike;
  dispatch: ResolveDispatch;
  onNavigate?: (frontendUrl: string) => void;
}> = ({ task, dispatch, onNavigate }) => (
  <Stack spacing={1.5}>
    <Alert severity="warning">
      AI-генератор не получил данных (ни аудиторий, ни инсайтов). Открой диалог заново
      и выбери хотя бы один источник в CompositeDataPicker.
    </Alert>
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Button
        variant="contained"
        size="small"
        onClick={() => dispatch('EMPTY_SOURCE_DATA', 'reopen-dialog')}
      >
        Открыть диалог заново
      </Button>
      <OpenEntityButton task={task} onNavigate={onNavigate} lang="ru" />
      <CopyTaskIdButton task={task} lang="ru" />
    </Stack>
  </Stack>
);

const ResolveAiParseFailed: React.FC<{
  task: ErrorTaskLike;
  dispatch: ResolveDispatch;
  onNavigate?: (frontendUrl: string) => void;
}> = ({ task, dispatch, onNavigate }) => (
  <Stack spacing={1.5}>
    <Alert severity="warning">
      AI вернул ответ в неожиданном формате. Обычно помогает простой повтор: модель
      стохастична, вторая попытка проходит.
    </Alert>
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Button
        variant="contained"
        size="small"
        startIcon={<ReplayIcon />}
        onClick={() => dispatch('AI_PARSE_FAILED', 'retry')}
        data-testid={TASKS_PANEL.errorResolverAction('retry')}
      >
        Retry
      </Button>
      <OpenEntityButton task={task} onNavigate={onNavigate} lang="ru" />
      <CopyTaskIdButton task={task} lang="ru" />
    </Stack>
  </Stack>
);

/**
 * GP-R32 / final review m20: the fallback panel of a REGISTERED task type whose code this
 * module knows nothing about. Its alert title used to be the one Russian line inside an
 * otherwise English panel (its own action row is English); it now follows the panel's
 * language like every other string here.
 */
const ResolveGeneric: React.FC<{
  task: ErrorTaskLike;
  dispatch: ResolveDispatch;
  onNavigate?: (frontendUrl: string) => void;
  lang?: PanelLang;
}> = ({ task, dispatch, onNavigate, lang = 'en' }) => (
  <Stack spacing={1}>
    <ErrorMessage
      title={lang === 'ru' ? 'Необработанная ошибка' : 'Unhandled error'}
      text={task.error ?? 'Unknown error'}
    />
    <Stack direction="row" spacing={1}>
      <Button
        variant="outlined"
        size="small"
        startIcon={<ReplayIcon />}
        onClick={() => dispatch(task.errorCode ?? 'UNKNOWN', 'retry')}
        data-testid={TASKS_PANEL.errorResolverAction('retry')}
      >
        Retry
      </Button>
      <OpenEntityButton task={task} onNavigate={onNavigate} lang={lang} />
      <CopyTaskIdButton task={task} lang={lang} />
    </Stack>
  </Stack>
);

/**
 * The registered half. Fix round 1 (I1): a press is never silent — the handler
 * ANSWERS whether it acted, and a `false` (an unknown code, or a surface that
 * unregistered between render and click) is stated inline instead of leaving the
 * owner in front of a button that appears to do nothing.
 */
const ResolveRegistered: React.FC<{
  task: ErrorTaskLike;
  eventName: string;
  onNavigate?: (frontendUrl: string) => void;
}> = ({ task, eventName, onNavigate }) => {
  const [notice, setNotice] = React.useState<string | null>(null);
  const dispatch = React.useCallback<ResolveDispatch>((code, action, meta) => {
    setNotice(dispatchResolve(eventName, code, action, task, meta) ? null : NOTHING_ACTED);
  }, [eventName, task]);

  const panel = (() => {
    switch (task.errorCode) {
      case 'INSUFFICIENT_BACKLOG':
        return <ResolveInsufficientBacklog task={task} dispatch={dispatch} onNavigate={onNavigate} />;
      case 'EMPTY_SOURCE_DATA':
        return <ResolveEmptySourceData task={task} dispatch={dispatch} onNavigate={onNavigate} />;
      case 'AI_PARSE_FAILED':
        return <ResolveAiParseFailed task={task} dispatch={dispatch} onNavigate={onNavigate} />;
      default:
        // FR-10: one list decides the panel's language and the heading above it.
        return (
          <ResolveGeneric
            task={task}
            dispatch={dispatch}
            onNavigate={onNavigate}
            lang={taskErrorResolverPanelLang(task, true)}
          />
        );
    }
  })();

  return (
    <Stack spacing={1}>
      {panel}
      {notice && (
        <Alert severity="warning" data-testid={TASKS_PANEL.errorResolverResult}>
          <Typography variant="body2">{notice}</Typography>
        </Alert>
      )}
    </Stack>
  );
};

/**
 * AC-GP-029: nobody can act on this task type, so the resolver states the failure
 * and offers only what always works — opening the entity and copying the id.
 */
const ResolveMessageOnly: React.FC<{
  task: ErrorTaskLike;
  onNavigate?: (frontendUrl: string) => void;
}> = ({ task, onNavigate }) => (
  <Stack spacing={1}>
    <ErrorMessage text={task.error ?? 'Unknown error'} />
    <Stack direction="row" spacing={1}>
      <OpenEntityButton task={task} onNavigate={onNavigate} />
      <CopyTaskIdButton task={task} />
    </Stack>
  </Stack>
);

// ─── Public component ──────────────────────────────────────────────────

export const TaskErrorResolver: React.FC<TaskErrorResolverProps> = ({
  task, eventName = 'task-error:resolve', onNavigate, api,
}) => {
  // Fix round 1 (M6): the dialog outlives a surface's mount, so the button follows
  // the registry LIVE — a late registration lights it, an unmount takes it away.
  const registered = React.useSyncExternalStore(
    subscribeTaskResolveHandlers,
    () => hasTaskResolveHandler(task.taskType),
    () => false,
  );

  if (!task.error && !task.errorCode) return null;

  const strategyFailure = classifyStrategyTaskFailure(task);
  if (strategyFailure) {
    return (
      <ResolveStrategyGeneration
        task={task}
        failure={strategyFailure}
        onNavigate={onNavigate}
        api={api}
      />
    );
  }

  if (!registered) {
    return <ResolveMessageOnly task={task} onNavigate={onNavigate} />;
  }

  return <ResolveRegistered task={task} eventName={eventName} onNavigate={onNavigate} />;
};

export default TaskErrorResolver;
